import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import Header from './componentsv2/Header';
import Footer from './componentsv2/Footer';
import SearchPanel from './componentsv2/SearchPanel';
import LatestJobsList from './componentsv2/LatestJobsList';
import AdSenseUnit from './componentsv2/AdSenseUnit';
import { classifyIndustry, normalizeIndustryValue } from './industryClassifier';
import { fetchJobsApiPayload } from './jobsApi';
import { AD_CLIENT, PRIVACY_CONTACT_EMAIL, useMonetizationGate } from './monetization';
import {
  getAllBlogPosts,
  getBlogPostBySlug,
  getBlogTagOptions,
  getRelatedBlogPosts,
} from './blog/blogData';

const BASE_SITE_URL = 'https://jobspostedtoday.com';
const JOB_DETAIL_CACHE_KEY = 'jpt_job_detail_cache_v1';

const PAGE_SIZE = 7;
const DESKTOP_RAIL_MIN_WIDTH = 1520;
const ENABLE_DESKTOP_RAIL_ADS =
  String(process.env.REACT_APP_ENABLE_DESKTOP_RAIL_ADS || '').toLowerCase() === 'true';
const ADSENSE_SIDEBAR_SLOT = process.env.REACT_APP_ADSENSE_SIDEBAR_SLOT || '';
const ADSENSE_LEFT_RAIL_SLOT = process.env.REACT_APP_ADSENSE_LEFT_RAIL_SLOT || ADSENSE_SIDEBAR_SLOT;
const ADSENSE_RIGHT_RAIL_SLOT =
  process.env.REACT_APP_ADSENSE_RIGHT_RAIL_SLOT || ADSENSE_SIDEBAR_SLOT;
const ADSENSE_INLINE_SLOT = process.env.REACT_APP_ADSENSE_INLINE_SLOT || ADSENSE_SIDEBAR_SLOT;
const HOME_PAGE_TITLE = 'Jobs Posted Today (Last 24 Hours) | JobsPostedToday';
const HOME_META_DESCRIPTION =
  process.env.REACT_APP_HOME_META_DESCRIPTION ||
  'Find the latest jobs posted in the last 24 hours. Browse fresh openings by city, company, and title, and discover jobs near me.';
const ABOUT_PAGE_TITLE = 'About Jobs Posted Today | USA Jobs Platform';
const ABOUT_META_DESCRIPTION =
  'Learn how JobsPostedToday helps people find jobs posted today, discover jobs near me, and explore companies hiring near me across the USA.';
const PRIVACY_PAGE_TITLE = 'Privacy Policy | JobsPostedToday';
const PRIVACY_PAGE_DESCRIPTION =
  'Read the JobsPostedToday privacy policy, including how advertising, cookies, and privacy choices work on the site.';
const JOBS_NEAR_ME_TITLE = 'Jobs Near Me | JobsPostedToday';
const JOBS_NEAR_ME_DESCRIPTION =
  'Find jobs near me with location-based search and city/state filters on JobsPostedToday.';
const BLOG_INDEX_TITLE = 'Job Search Blog | JobsPostedToday';
const BLOG_INDEX_DESCRIPTION =
  'Practical job-search guides and Top 10 jobs posted today roundups to help you apply early and focus on fresh openings.';

const STATE_PAGE_CONFIG = [
  { code: 'TX', name: 'Texas', slug: '/texas-jobs' },
  { code: 'CA', name: 'California', slug: '/california-jobs' },
  { code: 'FL', name: 'Florida', slug: '/florida-jobs' },
  { code: 'NY', name: 'New York', slug: '/new-york-jobs' },
];

const CITY_INTRO_MAP = {
  '/new-york-ny-jobs': [
    'See jobs posted in the last 24 hours in New York, NY and apply early when you find a fit.',
  ],
  '/austin-tx-jobs': [
    'Find jobs posted in the last 24 hours in Austin, TX and apply early to improve your chances.',
  ],
  '/atlanta-ga-jobs': [
    'Review jobs posted in the last 24 hours in Atlanta, GA and apply early when the role fits.',
  ],
  '/chicago-il-jobs': [
    'Browse jobs posted in the last 24 hours in Chicago, IL and apply early once you find a match.',
  ],
  '/seattle-wa-jobs': [
    'See jobs posted in the last 24 hours in Seattle, WA and apply early when a role matches your skills.',
  ],
  '/boston-ma-jobs': [
    'Discover jobs posted in the last 24 hours in Boston, MA and apply early when your background fits.',
  ],
  '/minneapolis-mn-jobs': [
    'Check jobs posted in the last 24 hours in Minneapolis, MN and apply early while listings are new.',
  ],
  '/salt-lake-city-ut-jobs': [
    'View jobs posted in the last 24 hours in Salt Lake City, UT and apply early when you find a match.',
  ],
};

const STATE_NAME_TO_CODE = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
};

const BASE_MOCK_SEARCH_JOBS = [
  {
    id: 'mock-1',
    title: 'Front-End Developer',
    company: 'Oracle',
    location: 'Austin, TX',
    jobType: 'Full-Time',
    workMode: 'Hybrid',
    experienceLevel: 'Mid',
    collecttime: Date.now() - 1000 * 60 * 22,
    url: '',
  },
  {
    id: 'mock-2',
    title: 'Customer Support Specialist',
    company: 'CVS Health',
    location: 'Remote, US',
    jobType: 'Part-Time',
    workMode: 'Remote',
    experienceLevel: 'Entry',
    collecttime: Date.now() - 1000 * 60 * 35,
    url: '',
  },
  {
    id: 'mock-3',
    title: 'Sales Associate',
    company: 'Walgreens',
    location: 'Boston, MA',
    jobType: 'Contract',
    workMode: 'On-Site',
    experienceLevel: 'Mid',
    collecttime: Date.now() - 1000 * 60 * 48,
    url: '',
  },
  {
    id: 'mock-4',
    title: 'Data Analyst',
    company: 'Aerotek',
    location: 'Chicago, IL',
    jobType: 'Full-Time',
    workMode: 'Remote',
    experienceLevel: 'Senior',
    collecttime: Date.now() - 1000 * 60 * 61,
    url: '',
  },
  {
    id: 'mock-5',
    title: 'Marketing Intern',
    company: 'CDM Smith',
    location: 'New York, NY',
    jobType: 'Internship',
    workMode: 'On-Site',
    experienceLevel: 'Entry',
    collecttime: Date.now() - 1000 * 60 * 79,
    url: '',
  },
  {
    id: 'mock-6',
    title: 'DevOps Engineer',
    company: 'Google',
    location: 'Remote, US',
    jobType: 'Full-Time',
    workMode: 'Remote',
    experienceLevel: 'Senior',
    collecttime: Date.now() - 1000 * 60 * 95,
    url: '',
  },
  {
    id: 'mock-7',
    title: 'Warehouse Associate',
    company: 'Amazon',
    location: 'Dallas, TX',
    jobType: 'Temporary',
    workMode: 'On-Site',
    experienceLevel: 'Entry',
    collecttime: Date.now() - 1000 * 60 * 120,
    url: '',
  },
  {
    id: 'mock-8',
    title: 'Nurse Practitioner',
    company: 'UnitedHealth',
    location: 'Phoenix, AZ',
    jobType: 'Per Diem',
    workMode: 'On-Site',
    experienceLevel: 'Mid',
    collecttime: Date.now() - 1000 * 60 * 135,
    url: '',
  },
  {
    id: 'mock-9',
    title: 'QA Tester',
    company: 'Microsoft',
    location: 'Seattle, WA',
    jobType: 'Contract',
    workMode: 'Hybrid',
    experienceLevel: 'Mid',
    collecttime: Date.now() - 1000 * 60 * 165,
    url: '',
  },
  {
    id: 'mock-10',
    title: 'Project Manager',
    company: 'Tesla',
    location: 'San Francisco, CA',
    jobType: 'Full-Time',
    workMode: 'Hybrid',
    experienceLevel: 'Senior',
    collecttime: Date.now() - 1000 * 60 * 184,
    url: '',
  },
];

const MOCK_SEARCH_JOBS = Array.from({ length: 5 }).flatMap((_, batchIndex) =>
  BASE_MOCK_SEARCH_JOBS.map((job, itemIndex) => ({
    ...job,
    id: `${job.id}-b${batchIndex + 1}`,
    collecttime:
      Date.now() - (batchIndex * BASE_MOCK_SEARCH_JOBS.length + itemIndex + 1) * 1000 * 60 * 11,
  }))
);

const SAMPLE_TOP_COMPANIES = [
  { company: 'Oracle', count: 1420 },
  { company: 'CVS Health', count: 838 },
  { company: 'Aerotek', count: 687 },
  { company: 'Walgreens', count: 659 },
  { company: 'CDM Smith', count: 576 },
];

const SAMPLE_TOP_CITIES = [
  { location: 'NEW YORK, NY', count: 395 },
  { location: 'Atlanta, GA', count: 331 },
  { location: 'Austin, TX', count: 304 },
  { location: 'Boston, MA', count: 254 },
  { location: 'Chicago, IL', count: 243 },
];

const toMillis = (value) => {
  if (!value) return NaN;
  if (typeof value === 'number') return value;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? NaN : parsed;
};

const normalizeToken = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const normalizeDisplayKey = (value) =>
  normalizeToken(value)
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ');

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toCityRouteSlug = (city, state) => `/${slugify(city)}-${String(state || '').toLowerCase()}-jobs`;

const getJobId = (job) =>
  String(
    job?.id ||
      job?.jobId ||
      job?.jvid ||
      job?.docId ||
      job?.docid ||
      job?.jobid ||
      ''
  ).trim();

const toJobIdKey = (value) => String(value || '').trim().toUpperCase();

const getRawJobLink = (job) =>
  job?.url ||
  job?.link ||
  job?.joburl ||
  job?.jobUrl ||
  job?.joblink ||
  job?.jobLink ||
  job?.applyurl ||
  job?.applyUrl ||
  job?.applylink ||
  job?.applyLink ||
  job?.apply_url ||
  job?.job_url ||
  job?.source_url ||
  job?.sourceurl ||
  job?.sourceUrl ||
  '';

const normalizeExternalLinkCandidate = (rawLink) => {
  const value = String(rawLink || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(value)) return `https://${value}`;
  return value;
};

const sanitizeExternalJobLink = (link) => {
  if (!link || typeof link !== 'string') return '';
  try {
    const parsed = new URL(
      normalizeExternalLinkCandidate(link),
      typeof window !== 'undefined' ? window.location.origin : BASE_SITE_URL
    );
    const host = (parsed.hostname || '').toLowerCase();
    const currentHost = typeof window !== 'undefined' ? (window.location.hostname || '').toLowerCase() : '';
    const isOwnProjectHost =
      host.includes('jobsprojectv1.web.app') ||
      host.includes('jobsprojectv1.firebaseapp.com') ||
      host.includes('jobsprojectv1') ||
      host.includes('jobspostedtoday.com');
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    if (currentHost && host === currentHost) return '';
    if (host === 'localhost' || host === '127.0.0.1') return '';
    if (isOwnProjectHost) return '';
    return parsed.href;
  } catch (error) {
    return '';
  }
};

const inferWorkMode = (job) => {
  const explicit = String(job?.workMode || job?.mode || '').trim();
  if (explicit) return explicit;
  const haystack = `${job?.title || ''} ${job?.location || ''}`.toLowerCase();
  if (haystack.includes('remote')) return 'Remote';
  if (haystack.includes('hybrid')) return 'Hybrid';
  return 'On-Site';
};

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'just now';
  const value =
    typeof timestamp === 'object' && typeof timestamp.toMillis === 'function'
      ? timestamp.toMillis()
      : typeof timestamp === 'number'
      ? timestamp
      : Date.parse(timestamp);
  if (Number.isNaN(value)) return 'recently';
  const diffMinutes = Math.max(1, Math.floor((Date.now() - value) / 1000 / 60));
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

const buildJobDetailPathFromValues = (jobId, title, company, location) => {
  const safeId = String(jobId || '').trim();
  if (!safeId) return '';
  const slugBase = slugify(`${title || ''} ${company || ''} ${location || ''}`) || 'job';
  return `/jobs/${encodeURIComponent(safeId)}/${slugBase}`;
};

const parseJobDetailRoute = (pathValue) => {
  const raw = String(pathValue || '').trim();
  const noTrailingSlash = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
  const match = noTrailingSlash.match(/^\/jobs\/([^/]+)(?:\/([^/]+))?$/i);
  if (!match) return null;
  let decodedId = match[1];
  try {
    decodedId = decodeURIComponent(match[1]);
  } catch (error) {
    decodedId = match[1];
  }
  const jobId = String(decodedId || '').trim();
  if (!jobId) return null;
  return {
    jobId,
    slug: String(match[2] || '').trim(),
  };
};

const readJobDetailCache = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(JOB_DETAIL_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeJobDetailCache = (cache) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(JOB_DETAIL_CACHE_KEY, JSON.stringify(cache || {}));
  } catch (error) {
    // ignore storage errors
  }
};

const saveJobDetailToCache = (job) => {
  const normalized = normalizeJobRecord(job || {});
  const jobIdKey = toJobIdKey(getJobId(normalized));
  if (!jobIdKey) return;
  const cache = readJobDetailCache();
  cache[jobIdKey] = {
    ...normalized,
    cachedAt: Date.now(),
  };
  const entries = Object.entries(cache).sort(
    (a, b) => Number(b[1]?.cachedAt || 0) - Number(a[1]?.cachedAt || 0)
  );
  const trimmed = Object.fromEntries(entries.slice(0, 500));
  writeJobDetailCache(trimmed);
};

const getJobDetailFromCache = (jobId) => {
  const cache = readJobDetailCache();
  const job = cache?.[toJobIdKey(jobId)];
  if (!job) return null;
  return normalizeJobRecord(job);
};

const normalizePath = (pathValue) => {
  const raw = String(pathValue || '/').trim() || '/';
  const withoutTrailingSlash = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
  if (/^\/jobs\//i.test(withoutTrailingSlash)) return withoutTrailingSlash;
  if (withoutTrailingSlash.length > 1) return withoutTrailingSlash.toLowerCase();
  return raw.toLowerCase();
};

const createEmptyFilters = () => ({
  companies: [],
  cities: [],
  states: [],
  standardTitles: [],
  industries: [],
  workModes: [],
  experienceLevels: [],
});

const normalizeFilterList = (value) =>
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

const normalizeFilterState = (filters) => ({
  companies: normalizeFilterList(filters?.companies),
  cities: normalizeFilterList(filters?.cities),
  states: normalizeFilterList(filters?.states),
  standardTitles: normalizeFilterList(filters?.standardTitles),
  industries: normalizeFilterList(filters?.industries),
  workModes: normalizeFilterList(filters?.workModes),
  experienceLevels: normalizeFilterList(filters?.experienceLevels),
});

const buildListHistoryState = ({ path, queryText, searchText, filters, page }) => ({
  kind: 'job-list',
  version: 1,
  path: normalizePath(path || '/'),
  queryText: String(queryText || ''),
  searchText: String(searchText ?? queryText ?? ''),
  filters: normalizeFilterState(filters),
  page: Math.max(1, Number.parseInt(page, 10) || 1),
});

const readListHistoryState = (state) => {
  if (!state || state.kind !== 'job-list' || state.version !== 1) return null;
  return {
    path: normalizePath(state.path || '/'),
    queryText: String(state.queryText || ''),
    searchText: String(state.searchText || state.queryText || ''),
    filters: normalizeFilterState(state.filters),
    page: Math.max(1, Number.parseInt(state.page, 10) || 1),
  };
};

const toCanonicalUrl = (pathValue) => {
  const normalizedPath = normalizePath(pathValue || '/');
  if (normalizedPath === '/') return `${BASE_SITE_URL}/`;
  return `${BASE_SITE_URL}${normalizedPath}`;
};

const setMetaTagContent = (name, content) => {
  if (!name) return;
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setMetaPropertyContent = (property, content) => {
  if (!property) return;
  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setCanonicalHref = (href) => {
  let element = document.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const shouldNoindexCurrentUrl = () => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search || '').toString().length > 0;
};

const inferStateCode = (stateRaw) => {
  const stateText = String(stateRaw || '').trim();
  if (!stateText) return '';
  if (/^[A-Za-z]{2}$/.test(stateText)) return stateText.toUpperCase();
  return STATE_NAME_TO_CODE[stateText.toLowerCase()] || '';
};

const parseLocationTokens = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split(',').map((item) => item.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const state = inferStateCode(parts[parts.length - 1]);
  if (!state) return null;
  const city = parts[0];
  return {
    city: city.replace(/\s+/g, ' ').trim(),
    state,
    location: `${city.replace(/\s+/g, ' ').trim()}, ${state}`,
  };
};

const buildGenericCityIntro = (city, state) => [
  `Explore jobs posted in the last 24 hours in ${city}, ${state} and apply early when a role matches your goals.`,
];

const formatDisplayDate = (dateValue) => {
  const parsed = Date.parse(dateValue);
  if (Number.isNaN(parsed)) return String(dateValue || '');
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(parsed));
};

const getStandardJobTitle = (job) =>
  String(job?.standardJobTitle || job?.standardjobtitle || job?.standard_title || '').trim();

const normalizeExperienceLevelValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.toLowerCase() === 'unknown') return '';
  return normalized;
};

const toOptionalInt = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatExperienceDisplay = (job) => {
  const level = normalizeExperienceLevelValue(
    job?.experienceLevel || job?.experiencelevel || job?.experience_level
  );
  const minYears = toOptionalInt(
    job?.experienceMinYears || job?.experienceminyears || job?.experience_min_years
  );
  const maxYears = toOptionalInt(
    job?.experienceMaxYears || job?.experiencemaxyears || job?.experience_max_years
  );

  let yearsLabel = '';
  if (minYears !== null && maxYears !== null) {
    if (minYears === maxYears) {
      yearsLabel = `${minYears} year${minYears === 1 ? '' : 's'}`;
    } else {
      yearsLabel = `${minYears}-${maxYears} years`;
    }
  } else if (minYears !== null) {
    yearsLabel = minYears === 1 ? '1 year' : `${minYears}+ years`;
  } else if (maxYears !== null) {
    yearsLabel = maxYears === 1 ? '1 year' : `${maxYears} years`;
  }

  if (yearsLabel && level) return `${yearsLabel} · ${level}`;
  if (yearsLabel) return yearsLabel;
  if (level) return level;
  return '';
};

const attachIndustryToJob = (job) => {
  const industry = classifyIndustry(job?.title || '', getStandardJobTitle(job));
  return {
    ...job,
    industry,
  };
};

const normalizeJobRecord = (job) => {
  const withIndustry = attachIndustryToJob(job || {});
  const jobId = getJobId(withIndustry);
  const title = String(withIndustry?.title || withIndustry?.jobtitle || 'Job Title').trim();
  const company = String(withIndustry?.company || withIndustry?.employer || 'Company').trim();
  const location = String(withIndustry?.location || 'Location').trim();
  const postedtime =
    Number(withIndustry?.postedtime) || Number(withIndustry?.collecttime) || Date.now();
  const collecttime = Number(withIndustry?.collecttime) || postedtime;
  const detailPath = buildJobDetailPathFromValues(jobId, title, company, location);
  const applyUrl = sanitizeExternalJobLink(getRawJobLink(withIndustry));
  const summaryCandidates = [
    withIndustry?.summary,
    withIndustry?.description,
    withIndustry?.snippet,
    withIndustry?.jobSummary,
    withIndustry?.jobsummary,
    withIndustry?.jobDescription,
    withIndustry?.jobdescription,
  ];
  const summaryRaw = summaryCandidates.find((value) => String(value || '').trim().length > 0);
  const summary =
    String(summaryRaw || '').trim() ||
    `${title} at ${company} in ${location}. Apply early while this listing is still fresh.`;
  const experienceLevel = normalizeExperienceLevelValue(
    withIndustry?.experienceLevel || withIndustry?.experiencelevel || withIndustry?.experience_level
  );
  const experienceMinYears = toOptionalInt(
    withIndustry?.experienceMinYears || withIndustry?.experienceminyears || withIndustry?.experience_min_years
  );
  const experienceMaxYears = toOptionalInt(
    withIndustry?.experienceMaxYears || withIndustry?.experiencemaxyears || withIndustry?.experience_max_years
  );
  const experienceDisplay = formatExperienceDisplay(withIndustry);

  return {
    ...withIndustry,
    id: jobId || withIndustry?.id,
    title,
    company,
    location,
    postedtime,
    collecttime,
    jobType: String(withIndustry?.jobType || withIndustry?.type || 'Full-Time').trim() || 'Full-Time',
    workMode: inferWorkMode(withIndustry),
    experienceLevel,
    experienceMinYears,
    experienceMaxYears,
    experienceDisplay,
    summary,
    detailPath,
    applyUrl,
  };
};

const pickRelatedJobs = (targetJob, candidates, limit = 6) => {
  const targetId = toJobIdKey(getJobId(targetJob));
  const unique = new Map();
  (candidates || []).forEach((job) => {
    const normalized = normalizeJobRecord(job);
    const jobId = toJobIdKey(getJobId(normalized));
    if (!jobId || jobId === targetId) return;
    if (unique.has(jobId)) return;
    unique.set(jobId, normalized);
  });

  return Array.from(unique.values())
    .map((job) => {
      let score = 0;
      if (normalizeToken(job.company) === normalizeToken(targetJob.company)) score += 100;
      if (normalizeToken(job.location) === normalizeToken(targetJob.location)) score += 60;
      if (normalizeToken(getStandardJobTitle(job)) === normalizeToken(getStandardJobTitle(targetJob))) score += 25;
      if (normalizeToken(job.jobType) === normalizeToken(targetJob.jobType)) score += 10;
      score += Math.max(0, Math.floor((Date.now() - toMillis(job.postedtime)) / -1000 / 60));
      return { job, score };
    })
    .sort((a, b) => b.score - a.score || toMillis(b.job.postedtime) - toMillis(a.job.postedtime))
    .slice(0, limit)
    .map((item) => item.job);
};

const buildLocationIndexFromLocations = (locations) => {
  const stateMap = new Map();
  const cityMap = new Map();
  (locations || []).forEach((locationValue) => {
    const parsed = parseLocationTokens(locationValue);
    if (!parsed) return;
    const stateCount = (stateMap.get(parsed.state) || 0) + 1;
    stateMap.set(parsed.state, stateCount);
    const cityKey = `${parsed.city}|${parsed.state}`;
    const cityCount = (cityMap.get(cityKey)?.count || 0) + 1;
    cityMap.set(cityKey, { city: parsed.city, state: parsed.state, location: parsed.location, count: cityCount });
  });
  const states = Array.from(stateMap.entries())
    .map(([state, count]) => ({ state, count }))
    .sort((a, b) => b.count - a.count);
  const cities = Array.from(cityMap.values()).sort((a, b) => b.count - a.count);
  return { states, cities };
};

const groupForDisplay = (items, keyField) => {
  const map = new Map();
  (items || []).forEach((item) => {
    const raw = String(item?.[keyField] || '').replace(/\s+/g, ' ').trim();
    if (!raw) return;
    const key = normalizeDisplayKey(raw);
    const count = Number(item?.count) || 0;
    if (!map.has(key)) {
      map.set(key, { label: raw, count: 0 });
    }
    const curr = map.get(key);
    curr.count += count;
    const currHasLower = /[a-z]/.test(curr.label);
    const rawHasLower = /[a-z]/.test(raw);
    if (!currHasLower && rawHasLower) curr.label = raw;
  });
  return Array.from(map.values())
    .map((v) => ({ [keyField]: v.label, count: v.count }))
    .sort((a, b) => b.count - a.count);
};

const buildTop = (items, fields, outputKey) => {
  const counts = new Map();
  items.forEach((item) => {
    let value = '';
    for (const field of fields) {
      if (item?.[field]) {
        value = String(item[field]).trim();
        break;
      }
    }
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ [outputKey]: name, count }))
    .sort((a, b) => b.count - a.count);
};

const runMockSearch = ({ queryText = '', limit = PAGE_SIZE, offset = 0, filters = {} }) => {
  const queryValue = queryText.trim().toLowerCase();
  const normalizedCompanySet = new Set((filters.companies || []).map(normalizeToken));
  const normalizedCitySet = new Set((filters.cities || []).map(normalizeToken));
  const normalizedStateSet = new Set((filters.states || []).map(normalizeToken));
  const normalizedStandardTitleSet = new Set((filters.standardTitles || []).map(normalizeToken));
  const normalizedIndustrySet = new Set(
    (filters.industries || [])
      .map((industry) => normalizeIndustryValue(industry))
      .filter(Boolean)
  );
  const jobsWithIndustry = MOCK_SEARCH_JOBS.map((job) => attachIndustryToJob(job));
  const filtered = jobsWithIndustry.filter((job) => {
    const haystack = `${job.title || ''} ${job.company || ''} ${job.location || ''} ${
      job.jobType || ''
    } ${job.workMode || ''} ${job.experienceLevel || ''}`.toLowerCase();
    const keywordMatch = queryValue ? haystack.includes(queryValue) : true;
    const companyMatch = normalizedCompanySet.size
      ? normalizedCompanySet.has(normalizeToken(job.company))
      : true;
    const cityMatch = normalizedCitySet.size
      ? normalizedCitySet.has(normalizeToken(job.location))
      : true;
    const stateToken = String(job.location || '').split(',')[1] || '';
    const stateMatch = normalizedStateSet.size
      ? normalizedStateSet.has(normalizeToken(stateToken))
      : true;
    const standardTitleValue = getStandardJobTitle(job);
    const standardTitleMatch = normalizedStandardTitleSet.size
      ? Boolean(standardTitleValue) &&
        normalizedStandardTitleSet.has(normalizeToken(standardTitleValue))
      : true;
    const workModeMatch = filters.workModes?.length
      ? filters.workModes.includes(job.workMode)
      : true;
    const expMatch = filters.experienceLevels?.length
      ? filters.experienceLevels.includes(job.experienceLevel)
      : true;
    const industryMatch = normalizedIndustrySet.size
      ? normalizedIndustrySet.has(normalizeIndustryValue(job.industry))
      : true;
    return (
      keywordMatch &&
      companyMatch &&
      cityMatch &&
      stateMatch &&
      standardTitleMatch &&
      workModeMatch &&
      expMatch &&
      industryMatch
    );
  });

  const sorted = filtered.sort((a, b) => toMillis(b.collecttime) - toMillis(a.collecttime));
  const items = sorted.slice(offset, offset + limit);
  return {
    query: queryText,
    limit,
    offset,
    total: sorted.length,
    count: items.length,
    windowHours: 24,
    appliedFilters: filters,
    items,
    topCompanies: buildTop(sorted, ['company'], 'company').slice(0, 5),
    topCities: buildTop(sorted, ['location'], 'location').slice(0, 5),
  };
};

const toEncodedCsvParam = (arr) =>
  arr && arr.length
    ? arr
        .map((item) => encodeURIComponent(String(item)))
        .filter((item) => item.length > 0)
        .join(',')
    : '';

const setListParam = (params, key, values) => {
  const value = toEncodedCsvParam(values);
  if (value) params.set(key, value);
};

const hasAnyFilters = (filters) =>
  Boolean(
    (filters.companies && filters.companies.length) ||
      (filters.cities && filters.cities.length) ||
      (filters.states && filters.states.length) ||
      (filters.standardTitles && filters.standardTitles.length) ||
      (filters.industries && filters.industries.length) ||
      (filters.workModes && filters.workModes.length) ||
      (filters.experienceLevels && filters.experienceLevels.length)
  );

const App = () => {
  const [searchText, setSearchText] = useState('');
  const [response, setResponse] = useState(null);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [defaultFilterOptions, setDefaultFilterOptions] = useState({
    companies: [],
    cities: [],
    standardTitles: [],
  });
  const [activeQuery, setActiveQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState(createEmptyFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [routePath, setRoutePath] = useState(() => normalizePath(window.location.pathname || '/'));
  const pendingListRestoreRef = useRef(null);
  const [locationIndex, setLocationIndex] = useState({ states: [], cities: [] });
  const [nearMeStatus, setNearMeStatus] = useState('idle');
  const [nearMeTarget, setNearMeTarget] = useState({ stateCode: '', location: '' });
  const [jobDetailState, setJobDetailState] = useState({
    status: 'idle',
    job: null,
    relatedJobs: [],
    message: '',
  });
  const [blogTagFilter, setBlogTagFilter] = useState('all');
  const [blogSearchText, setBlogSearchText] = useState('');
  const [isDesktopRailViewport, setIsDesktopRailViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_RAIL_MIN_WIDTH : false
  );
  const allBlogPosts = useMemo(() => getAllBlogPosts(), []);

  const normalizedSearch = activeQuery.trim().toLowerCase();
  const cityRouteLinks = useMemo(() => {
    const items = Array.isArray(locationIndex?.cities) ? locationIndex.cities : [];
    return items
      .filter((item) => item?.city && item?.state && item?.location && Number(item?.count) > 0)
      .slice(0, 30)
      .map((item) => ({
        city: item.city,
        state: String(item.state).toUpperCase(),
        location: item.location,
        count: Number(item.count) || 0,
        slug: toCityRouteSlug(item.city, item.state),
      }));
  }, [locationIndex]);

  const stateRouteLinks = useMemo(() => {
    const rows = Array.isArray(locationIndex?.states) ? locationIndex.states : [];
    const countByCode = new Map(
      rows
        .filter((row) => row?.state)
        .map((row) => [String(row.state).toUpperCase(), Number(row.count) || 0])
    );
    const derived = STATE_PAGE_CONFIG.map((stateItem) => ({
      ...stateItem,
      count: countByCode.get(stateItem.code) || 0,
    })).filter((item) => item.count > 0);
    if (derived.length) return derived;
    return STATE_PAGE_CONFIG.map((stateItem) => ({ ...stateItem, count: 0 }));
  }, [locationIndex]);

  const routeInfo = useMemo(() => {
    if (routePath === '/about') return { kind: 'about' };
    if (routePath === '/privacy') return { kind: 'privacy' };
    if (routePath === '/blog') return { kind: 'blog-index' };
    if (routePath.startsWith('/blog/')) {
      return { kind: 'blog-post', slug: routePath.replace('/blog/', '').trim() };
    }
    const jobDetailRoute = parseJobDetailRoute(routePath);
    if (jobDetailRoute) return { kind: 'job-detail', ...jobDetailRoute };
    if (routePath === '/jobs-near-me') return { kind: 'jobs-near-me' };
    const stateMatch = stateRouteLinks.find((item) => item.slug === routePath);
    if (stateMatch) return { kind: 'state', ...stateMatch };
    const cityMatch = cityRouteLinks.find((item) => item.slug === routePath);
    if (cityMatch) return { kind: 'city', ...cityMatch };
    return { kind: 'home' };
  }, [routePath, cityRouteLinks, stateRouteLinks]);

  const isAboutPage = routeInfo.kind === 'about';
  const isPrivacyPage = routeInfo.kind === 'privacy';
  const isBlogPage = routeInfo.kind === 'blog-index' || routeInfo.kind === 'blog-post';
  const isJobDetailPage = routeInfo.kind === 'job-detail';
  const monetization = useMonetizationGate();
  const canServeAds = monetization.canServeAds;
  const shouldShowRouteAds = !isAboutPage && !isPrivacyPage;
  const hasRailInventory =
    ENABLE_DESKTOP_RAIL_ADS && Boolean(AD_CLIENT && (ADSENSE_LEFT_RAIL_SLOT || ADSENSE_RIGHT_RAIL_SLOT));
  const hasInlineInventory = Boolean(AD_CLIENT && ADSENSE_INLINE_SLOT);
  const shouldRenderRailColumns =
    shouldShowRouteAds &&
    hasRailInventory &&
    isDesktopRailViewport &&
    monetization.adsPermission !== 'blocked';
  const shouldRenderInlineFallback =
    shouldShowRouteAds && hasInlineInventory && !isDesktopRailViewport;
  const activeJobDetail = jobDetailState.job;
  const relatedJobsForDetail = jobDetailState.relatedJobs || [];
  const activeBlogPost = useMemo(
    () =>
      routeInfo.kind === 'blog-post' ? getBlogPostBySlug(routeInfo.slug, allBlogPosts) : null,
    [routeInfo, allBlogPosts]
  );
  const blogTagOptions = useMemo(() => getBlogTagOptions(allBlogPosts), [allBlogPosts]);
  const filteredBlogPosts = useMemo(() => {
    const query = blogSearchText.trim().toLowerCase();
    return allBlogPosts.filter((post) => {
      const tagMatch = blogTagFilter === 'all' || (post.tags || []).includes(blogTagFilter);
      const textMatch = query
        ? `${post.title} ${post.description} ${(post.tags || []).join(' ')}`
            .toLowerCase()
            .includes(query)
        : true;
      return tagMatch && textMatch;
    });
  }, [allBlogPosts, blogTagFilter, blogSearchText]);
  const relatedBlogPosts = useMemo(
    () => (activeBlogPost ? getRelatedBlogPosts(activeBlogPost, allBlogPosts, 3) : []),
    [activeBlogPost, allBlogPosts]
  );
  const isSearchMode = normalizedSearch.length > 0 || hasAnyFilters(activeFilters);

  const fetchJobsPage = async (queryText, filters, pageNumber) => {
    setJobsLoaded(false);
    try {
      const params = new URLSearchParams();
      params.set('q', queryText || '');
      params.set('page', String(pageNumber));
      params.set('pageSize', String(PAGE_SIZE));
      setListParam(params, 'companies', filters.companies);
      setListParam(params, 'cities', filters.cities);
      setListParam(params, 'states', filters.states);
      setListParam(params, 'standardTitles', filters.standardTitles);
      setListParam(params, 'industries', filters.industries);
      setListParam(params, 'workModes', filters.workModes);
      setListParam(params, 'experienceLevels', filters.experienceLevels);

      const payload = await fetchJobsApiPayload(params);
      const normalizedStandardTitleSet = new Set(
        (filters?.standardTitles || []).map((title) => normalizeToken(title))
      );
      const incomingItems = Array.isArray(payload?.items)
        ? payload.items.map((job) => attachIndustryToJob(job))
        : [];
      const filteredByStandardTitle = normalizedStandardTitleSet.size
        ? incomingItems.filter((job) =>
            Boolean(getStandardJobTitle(job)) &&
            normalizedStandardTitleSet.has(normalizeToken(getStandardJobTitle(job)))
          )
        : incomingItems;

      setResponse({
        ...payload,
        total: normalizedStandardTitleSet.size
          ? filteredByStandardTitle.length
          : Number(payload?.total) || filteredByStandardTitle.length,
        count: filteredByStandardTitle.length,
        items: filteredByStandardTitle,
        topCompanies: normalizedStandardTitleSet.size
          ? buildTop(filteredByStandardTitle, ['company'], 'company').slice(0, 5)
          : payload?.topCompanies,
        topCities: normalizedStandardTitleSet.size
          ? buildTop(filteredByStandardTitle, ['location'], 'location').slice(0, 5)
          : payload?.topCities,
      });
      if (!queryText.trim() && !hasAnyFilters(filters)) {
        const standardTitlesFromItems = Array.from(
          new Set(
            incomingItems
              .map((job) => (getStandardJobTitle(job) || '').trim())
              .filter(Boolean)
          )
        ).sort();
        setDefaultFilterOptions({
          companies: payload?.filterOptions?.companies || [],
          cities: payload?.filterOptions?.cities || [],
          standardTitles: payload?.filterOptions?.standardTitles || standardTitlesFromItems,
        });
      }
      setJobsLoaded(true);
    } catch (error) {
      const mockFallback = runMockSearch({
        queryText,
        limit: PAGE_SIZE,
        offset: (pageNumber - 1) * PAGE_SIZE,
        filters,
      });
      setResponse(mockFallback);
      setJobsLoaded(true);
    }
  };

  const routeLockedFilters = useMemo(() => {
    if (routeInfo.kind === 'state') {
      return { states: [routeInfo.code] };
    }
    if (routeInfo.kind === 'city') {
      return { states: [routeInfo.state], cities: [routeInfo.location] };
    }
    return {};
  }, [routeInfo]);

  const applyRouteFilters = (filters) => {
    const nextFilters = {
      ...createEmptyFilters(),
      ...normalizeFilterState(filters),
    };
    if (routeLockedFilters.states) nextFilters.states = routeLockedFilters.states;
    if (routeLockedFilters.cities) nextFilters.cities = routeLockedFilters.cities;
    return nextFilters;
  };

  const handleSearch = (payload) => {
    const queryText =
      typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'queryText')
        ? payload.queryText
        : searchText;
    const rawFilters =
      typeof payload === 'object' && payload.filters ? payload.filters : activeFilters;
    const filters = applyRouteFilters(rawFilters);
    setActiveQuery(queryText);
    setActiveFilters(filters);
    setCurrentPage(1);
    fetchJobsPage(queryText, filters, 1);
  };

  const handleGoHome = () => {
    const clearedFilters = createEmptyFilters();
    const homeState = buildListHistoryState({
      path: '/',
      queryText: '',
      searchText: '',
      filters: clearedFilters,
      page: 1,
    });
    const currentPath = normalizePath(window.location.pathname || routePath);
    pendingListRestoreRef.current = null;
    setActiveQuery('');
    setSearchText('');
    setActiveFilters(clearedFilters);
    setCurrentPage(1);
    setNearMeStatus('idle');
    setNearMeTarget({ stateCode: '', location: '' });
    if (currentPath !== '/') {
      window.history.pushState(homeState, '', '/');
      setRoutePath('/');
    } else {
      window.history.replaceState(homeState, '', '/');
      fetchJobsPage('', clearedFilters, 1);
    }
    window.scrollTo(0, 0);
  };

  const applyDashboardFilter = (nextFilterState) => {
    setSearchText('');
    handleSearch({
      queryText: '',
      filters: {
        ...createEmptyFilters(),
        ...nextFilterState,
      },
    });
    window.scrollTo(0, 0);
  };

  const handleTopCompanyClick = (companyName) => {
    const normalizedCompany = String(companyName || '').trim();
    if (!normalizedCompany) return;
    applyDashboardFilter({ companies: [normalizedCompany] });
  };

  const handleTopCityClick = (cityName) => {
    const normalizedCity = String(cityName || '').trim();
    if (!normalizedCity) return;
    applyDashboardFilter({ cities: [normalizedCity] });
  };

  const totalItems = response?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const jobs = useMemo(
    () => (response?.items || []).map((job) => normalizeJobRecord(job)),
    [response]
  );
  const jobsById = useMemo(() => {
    const map = new Map();
    jobs.forEach((job) => {
      const jobId = toJobIdKey(getJobId(job));
      if (jobId) map.set(jobId, job);
    });
    return map;
  }, [jobs]);

  useEffect(() => {
    jobs.slice(0, 200).forEach((job) => saveJobDetailToCache(job));
  }, [jobs]);
  const hideTopDashboards = isSearchMode && jobsLoaded && totalItems === 0;
  const emptyMessage = isSearchMode
    ? 'No jobs match your search. Try a different keyword.'
    : 'No jobs available right now.';

  const topCompanies = useMemo(() => {
    const source = response?.topCompanies?.length ? response.topCompanies : SAMPLE_TOP_COMPANIES;
    return groupForDisplay(source, 'company').slice(0, 5);
  }, [response]);

  const topCities = useMemo(() => {
    const source = response?.topCities?.length ? response.topCities : SAMPLE_TOP_CITIES;
    return groupForDisplay(source, 'location').slice(0, 5);
  }, [response]);

  const maxCompany = topCompanies.length ? topCompanies[0].count : 0;
  const maxCity = topCities.length ? topCities[0].count : 0;
  const isTopLoading = !jobsLoaded;
  const trendContextText = isSearchMode
    ? normalizedSearch
      ? 'Based on your search results'
      : 'Based on your selected filters'
    : '';

  const companyOptions = useMemo(() => {
    if (!isSearchMode && defaultFilterOptions.companies.length) {
      return defaultFilterOptions.companies;
    }
    if (response?.filterOptions?.companies?.length) {
      return response.filterOptions.companies;
    }
    return Array.from(new Set(MOCK_SEARCH_JOBS.map((item) => item.company))).sort();
  }, [response, isSearchMode, defaultFilterOptions]);

  const cityOptions = useMemo(() => {
    if (!isSearchMode && defaultFilterOptions.cities.length) {
      return defaultFilterOptions.cities;
    }
    if (response?.filterOptions?.cities?.length) {
      return response.filterOptions.cities;
    }
    return Array.from(new Set(MOCK_SEARCH_JOBS.map((item) => item.location))).sort();
  }, [response, isSearchMode, defaultFilterOptions]);

  const standardTitleOptions = useMemo(() => {
    if (!isSearchMode && defaultFilterOptions.standardTitles.length) {
      return defaultFilterOptions.standardTitles;
    }
    if (response?.filterOptions?.standardTitles?.length) {
      return response.filterOptions.standardTitles;
    }
    return Array.from(
      new Set(
        (response?.items || [])
          .map((item) => (getStandardJobTitle(item) || '').trim())
          .filter(Boolean)
      )
    ).sort();
  }, [response, isSearchMode, defaultFilterOptions]);

  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1).filter(
    (page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
  );
  const showLocationIntro = routeInfo.kind === 'jobs-near-me' || routeInfo.kind === 'state' || routeInfo.kind === 'city';
  const locationIntroTitle =
    routeInfo.kind === 'jobs-near-me'
      ? 'Jobs Near Me'
      : routeInfo.kind === 'state'
      ? `${routeInfo.name} Jobs`
      : routeInfo.kind === 'city'
      ? `${routeInfo.city}, ${routeInfo.state} Jobs`
      : '';
  const locationIntroText =
    routeInfo.kind === 'jobs-near-me'
      ? 'View local openings with automatic location detection and refine results using city and company filters.'
      : routeInfo.kind === 'state'
      ? `Explore active job openings across ${routeInfo.name} from companies hiring in top metro areas.`
      : '';
  const cityIntroLines =
    routeInfo.kind === 'city'
      ? CITY_INTRO_MAP[routeInfo.slug] || buildGenericCityIntro(routeInfo.city, routeInfo.state)
      : [];

  const fetchRelatedJobsForDetail = useCallback(async (targetJob) => {
    const candidateMap = new Map();
    const addCandidates = (items) => {
      (items || []).forEach((item) => {
        const normalized = normalizeJobRecord(item);
        const jobId = getJobId(normalized);
        if (!jobId || jobId === getJobId(targetJob)) return;
        if (!candidateMap.has(jobId)) candidateMap.set(jobId, normalized);
      });
    };

    const requestRelated = async ({ companies = [], cities = [] } = {}) => {
      const params = new URLSearchParams();
      params.set('q', '');
      params.set('page', '1');
      params.set('pageSize', '30');
      const companyValue = toEncodedCsvParam(companies);
      const cityValue = toEncodedCsvParam(cities);
      if (companyValue) params.set('companies', companyValue);
      if (cityValue) params.set('cities', cityValue);
      const payload = await fetchJobsApiPayload(params);
      addCandidates(payload?.items || []);
    };

    try {
      if (targetJob.company) await requestRelated({ companies: [targetJob.company] });
      if (targetJob.location && candidateMap.size < 12) await requestRelated({ cities: [targetJob.location] });
      if (candidateMap.size < 6) await requestRelated();
    } catch (error) {
      // fallback to in-memory rows below
    }

    addCandidates(jobs);
    return pickRelatedJobs(targetJob, Array.from(candidateMap.values()), 6);
  }, [jobs]);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return;
    setCurrentPage(nextPage);
    fetchJobsPage(activeQuery, activeFilters, nextPage);
  };

  useEffect(() => {
    const onPopState = (event) => {
      const nextPath = normalizePath(window.location.pathname || '/');
      const restoredState = readListHistoryState(event.state);
      pendingListRestoreRef.current =
        restoredState && restoredState.path === nextPath ? restoredState : null;
      setRoutePath(nextPath);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadLocationIndex = async () => {
      try {
        const payload = await fetchJobsApiPayload('view=locationIndex');
        if (!isMounted) return;
        const payloadStates = Array.isArray(payload?.states) ? payload.states : [];
        const payloadCities = Array.isArray(payload?.cities) ? payload.cities : [];
        if (payloadStates.length || payloadCities.length) {
          setLocationIndex({ states: payloadStates, cities: payloadCities });
          return;
        }
        const fallbackFromOptions = buildLocationIndexFromLocations(payload?.filterOptions?.cities || []);
        if (fallbackFromOptions.states.length || fallbackFromOptions.cities.length) {
          setLocationIndex(fallbackFromOptions);
          return;
        }
        const fallbackFromMock = buildLocationIndexFromLocations(
          MOCK_SEARCH_JOBS.map((item) => item.location)
        );
        setLocationIndex(fallbackFromMock);
      } catch (error) {
        if (!isMounted) return;
        const fallbackFromMock = buildLocationIndexFromLocations(
          MOCK_SEARCH_JOBS.map((item) => item.location)
        );
        setLocationIndex(fallbackFromMock);
      }
    };
    loadLocationIndex();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      setIsDesktopRailViewport(window.innerWidth >= DESKTOP_RAIL_MIN_WIDTH);
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    if (routeInfo.kind !== 'jobs-near-me') return;
    if (nearMeStatus !== 'idle') return;
    if (!navigator.geolocation) {
      setNearMeStatus('unavailable');
      return;
    }
    setNearMeStatus('resolving');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position?.coords?.latitude;
          const lon = position?.coords?.longitude;
          const reverseUrl =
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`;
          const geoRes = await fetch(reverseUrl, {
            method: 'GET',
            headers: { Accept: 'application/json' },
          });
          if (!geoRes.ok) throw new Error(`HTTP ${geoRes.status}`);
          const geoPayload = await geoRes.json();
          const address = geoPayload?.address || {};
          const cityName =
            address.city || address.town || address.village || address.hamlet || address.county || '';
          const stateCode = inferStateCode(address.state_code || address.state || '');
          const matchedCity = cityRouteLinks.find(
            (item) =>
              normalizeToken(item.city) === normalizeToken(cityName) &&
              normalizeToken(item.state) === normalizeToken(stateCode)
          );
          setNearMeTarget({
            stateCode,
            location: matchedCity ? matchedCity.location : '',
          });
          setNearMeStatus('resolved');
        } catch (error) {
          setNearMeStatus('failed');
        }
      },
      () => setNearMeStatus('failed'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }, [routeInfo.kind, nearMeStatus, cityRouteLinks]);

  useEffect(() => {
    if (!isJobDetailPage) {
      setJobDetailState((prev) =>
        prev.status === 'idle' && !prev.job && !prev.relatedJobs.length
          ? prev
          : { status: 'idle', job: null, relatedJobs: [], message: '' }
      );
      return;
    }

    let isCancelled = false;
    const localJob =
      jobsById.get(toJobIdKey(routeInfo.jobId)) ||
      getJobDetailFromCache(routeInfo.jobId) ||
      null;
    const fallbackRelated = localJob ? pickRelatedJobs(localJob, jobs, 6) : jobs.slice(0, 6);
    setJobDetailState({
      status: 'loading',
      job: localJob,
      relatedJobs: fallbackRelated,
      message: '',
    });

    const loadJobDetail = async () => {
      try {
        const params = new URLSearchParams();
        params.set('jobId', routeInfo.jobId);
        let payload = null;
        try {
          payload = await fetchJobsApiPayload(params);
        } catch (error) {
          payload = null;
        }
        const apiJob = payload?.item ? normalizeJobRecord(payload.item) : null;

        const findFromSlugFallback = async () => {
          const slugTokens = String(routeInfo.slug || '')
            .split('-')
            .map((item) => item.trim().toLowerCase())
            .filter((item) => item.length >= 3);
          if (!slugTokens.length) return null;

          const fallbackParams = new URLSearchParams();
          fallbackParams.set('q', slugTokens.slice(0, 8).join(' '));
          fallbackParams.set('page', '1');
          fallbackParams.set('pageSize', '100');
          let fallbackPayload = null;
          try {
            fallbackPayload = await fetchJobsApiPayload(fallbackParams);
          } catch (error) {
            return null;
          }
          const candidates = (fallbackPayload?.items || []).map((item) => normalizeJobRecord(item));
          if (!candidates.length) return null;

          const directIdMatch = candidates.find(
            (item) => toJobIdKey(getJobId(item)) === toJobIdKey(routeInfo.jobId)
          );
          if (directIdMatch) return directIdMatch;

          const scoreCandidate = (candidate) => {
            const haystack = `${candidate.title} ${candidate.company} ${candidate.location}`.toLowerCase();
            return slugTokens.reduce((score, token) => (haystack.includes(token) ? score + 1 : score), 0);
          };

          const scored = candidates
            .map((candidate) => ({ candidate, score: scoreCandidate(candidate) }))
            .sort((a, b) => b.score - a.score || toMillis(b.candidate.postedtime) - toMillis(a.candidate.postedtime));

          const best = scored[0];
          if (!best) return null;
          const minScore = Math.max(2, Math.ceil(slugTokens.length * 0.35));
          return best.score >= minScore ? best.candidate : null;
        };

        if (apiJob) {
          saveJobDetailToCache(apiJob);
          const relatedJobs = await fetchRelatedJobsForDetail(apiJob);
          if (isCancelled) return;
          setJobDetailState({
            status: 'ready',
            job: apiJob,
            relatedJobs,
            message: '',
          });
          return;
        }

        const slugMatchedJob = await findFromSlugFallback();
        if (slugMatchedJob) {
          saveJobDetailToCache(slugMatchedJob);
          const relatedJobs = await fetchRelatedJobsForDetail(slugMatchedJob);
          if (isCancelled) return;
          setJobDetailState({
            status: 'ready',
            job: slugMatchedJob,
            relatedJobs,
            message: '',
          });
          return;
        }

        if (localJob) {
          const relatedJobs = pickRelatedJobs(localJob, jobs, 6);
          if (isCancelled) return;
          setJobDetailState({
            status: 'ready',
            job: localJob,
            relatedJobs,
            message: '',
          });
          return;
        }

        const fallbackSeed = localJob || { id: routeInfo.jobId, company: '', location: '', jobType: '' };
        const relatedJobs = pickRelatedJobs(fallbackSeed, jobs, 6);
        if (isCancelled) return;
        setJobDetailState({
          status: 'missing',
          job: null,
          relatedJobs,
          message: 'This job is no longer available.',
        });
      } catch (error) {
        if (isCancelled) return;
        const fallbackSeed = localJob || { id: routeInfo.jobId, company: '', location: '', jobType: '' };
        const relatedJobs = pickRelatedJobs(fallbackSeed, jobs, 6);
        setJobDetailState({
          status: 'missing',
          job: null,
          relatedJobs,
          message: 'This job is no longer available.',
        });
      }
    };

    loadJobDetail();
    return () => {
      isCancelled = true;
    };
  }, [isJobDetailPage, jobs, jobsById, routeInfo.jobId, routeInfo.slug, fetchRelatedJobsForDetail]);

  const pageMeta = useMemo(() => {
    if (routeInfo.kind === 'blog-index') {
      return { title: BLOG_INDEX_TITLE, description: BLOG_INDEX_DESCRIPTION };
    }
    if (routeInfo.kind === 'blog-post') {
      if (activeBlogPost) {
        return {
          title: `${activeBlogPost.title} | JobsPostedToday Blog`,
          description: activeBlogPost.description || BLOG_INDEX_DESCRIPTION,
        };
      }
      return {
        title: 'Blog Post Not Found | JobsPostedToday',
        description: BLOG_INDEX_DESCRIPTION,
      };
    }
    if (routeInfo.kind === 'job-detail') {
      if (activeJobDetail) {
        return {
          title: `${activeJobDetail.title} at ${activeJobDetail.company} | Jobs Posted Today`,
          description: `${activeJobDetail.title} in ${activeJobDetail.location}. Posted ${formatRelativeTime(
            activeJobDetail.postedtime
          )}. Review details on JobsPostedToday and apply early.`,
        };
      }
      return {
        title: 'This Job is No Longer Available | JobsPostedToday',
        description:
          'This job is no longer available. Browse fresh jobs posted today and apply early to active listings.',
      };
    }
    if (routeInfo.kind === 'about') {
      return { title: ABOUT_PAGE_TITLE, description: ABOUT_META_DESCRIPTION };
    }
    if (routeInfo.kind === 'privacy') {
      return { title: PRIVACY_PAGE_TITLE, description: PRIVACY_PAGE_DESCRIPTION };
    }
    if (routeInfo.kind === 'jobs-near-me') {
      return { title: JOBS_NEAR_ME_TITLE, description: JOBS_NEAR_ME_DESCRIPTION };
    }
    if (routeInfo.kind === 'state') {
      return {
        title: `${routeInfo.name} Jobs | JobsPostedToday`,
        description: `Browse jobs posted today in ${routeInfo.name} and apply to active roles in major cities across the state.`,
      };
    }
    if (routeInfo.kind === 'city') {
      return {
        title: `${routeInfo.city}, ${routeInfo.state} Jobs | JobsPostedToday`,
        description: `See jobs posted today in ${routeInfo.city}, ${routeInfo.state}, and explore nearby opportunities from active employers.`,
      };
    }
    return { title: HOME_PAGE_TITLE, description: HOME_META_DESCRIPTION };
  }, [routeInfo, activeBlogPost, activeJobDetail]);

  useEffect(() => {
    const canonicalPath =
      routeInfo.kind === 'job-detail' && activeJobDetail?.detailPath
        ? activeJobDetail.detailPath
        : window.location.pathname || routePath;
    const canonicalUrl = toCanonicalUrl(canonicalPath);
    document.title = pageMeta.title;
    setMetaTagContent('description', pageMeta.description);
    setCanonicalHref(canonicalUrl);
    setMetaPropertyContent('og:title', pageMeta.title);
    setMetaPropertyContent('og:description', pageMeta.description);
    setMetaPropertyContent('og:url', canonicalUrl);
    setMetaPropertyContent('og:type', routeInfo.kind === 'job-detail' ? 'article' : 'website');
    setMetaTagContent('robots', shouldNoindexCurrentUrl() ? 'noindex,follow' : 'index,follow');
  }, [pageMeta, routePath, routeInfo.kind, activeJobDetail]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isAboutPage || isPrivacyPage || isBlogPage || isJobDetailPage) return;
    const historyState = buildListHistoryState({
      path: routePath,
      queryText: activeQuery,
      searchText,
      filters: activeFilters,
      page: currentPage,
    });
    window.history.replaceState(historyState, '', window.location.pathname || routePath);
  }, [isAboutPage, isPrivacyPage, isBlogPage, isJobDetailPage, routePath, activeQuery, searchText, activeFilters, currentPage]);

  useEffect(() => {
    if (isAboutPage || isPrivacyPage || isBlogPage || isJobDetailPage) return;
    const restoredState = pendingListRestoreRef.current;
    if (restoredState && restoredState.path === routePath) {
      pendingListRestoreRef.current = null;
      const restoredFilters = applyRouteFilters(restoredState.filters);
      const restoredQuery = restoredState.queryText;
      const restoredSearchText = restoredState.searchText || restoredQuery;
      const restoredPage = restoredState.page;
      setActiveQuery(restoredQuery);
      setSearchText(restoredSearchText);
      setActiveFilters(restoredFilters);
      setCurrentPage(restoredPage);
      fetchJobsPage(restoredQuery, restoredFilters, restoredPage);
      return;
    }
    const routeFilters = createEmptyFilters();
    if (routeInfo.kind === 'state') {
      routeFilters.states = [routeInfo.code];
    } else if (routeInfo.kind === 'city') {
      routeFilters.states = [routeInfo.state];
      routeFilters.cities = [routeInfo.location];
    } else if (routeInfo.kind === 'jobs-near-me' && nearMeStatus === 'resolved') {
      if (nearMeTarget.stateCode) routeFilters.states = [nearMeTarget.stateCode];
      if (nearMeTarget.location) routeFilters.cities = [nearMeTarget.location];
    }
    setActiveQuery('');
    setSearchText('');
    setActiveFilters(routeFilters);
    setCurrentPage(1);
    fetchJobsPage('', routeFilters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAboutPage, isPrivacyPage, isBlogPage, isJobDetailPage, routeInfo.kind, routeInfo.code, routeInfo.location, routeInfo.state, nearMeStatus, nearMeTarget.stateCode, nearMeTarget.location]);

  const navigateTo = (path) => {
    if (!path) return;
    const normalizedPath = normalizePath(path);
    if (window.location.pathname !== normalizedPath) {
      window.history.pushState({}, '', path);
    }
    setRoutePath(normalizedPath);
    if (normalizedPath !== '/jobs-near-me') {
      setNearMeStatus('idle');
      setNearMeTarget({ stateCode: '', location: '' });
    }
    window.scrollTo(0, 0);
  };

  const handleOpenPrivacyChoices = () => {
    const opened = monetization.openPrivacyChoices();
    if (!opened) {
      navigateTo('/privacy');
    }
  };

  const renderInlineAd = (wrapperClassName) => {
    if (!shouldRenderInlineFallback) return null;
    return (
      <AdSenseUnit
        slot={ADSENSE_INLINE_SLOT}
        format="auto"
        responsive
        enabled={canServeAds}
        label="Advertisement"
        wrapperClassName={wrapperClassName}
      />
    );
  };

  return (
    <>
      <AdSenseUnit bootstrapOnly />
      <div className={`site-frame ${shouldShowRouteAds ? 'site-frame-monetized' : ''}`.trim()}>
        {shouldRenderRailColumns ? (
          <aside className="site-rail site-rail-left" aria-label="Left advertisement rail">
            <AdSenseUnit
              slot={ADSENSE_LEFT_RAIL_SLOT}
              width={160}
              height={600}
              responsive={false}
              enabled={canServeAds}
              label="Advertisement"
              wrapperClassName="ad-rail-shell"
              className="ad-unit-fixed"
            />
          </aside>
        ) : null}

        <div className="site-main">
          <div className="page-shell">
            <section className="section-slot header-slot">
              <Header
                title={
                  isAboutPage
                    ? 'About Us'
                    : isPrivacyPage
                    ? 'Privacy'
                    : isBlogPage
                    ? 'Blog'
                    : isJobDetailPage
                    ? 'Job Details'
                    : 'Last 24 Hours Jobs'
                }
              />
            </section>

            {isAboutPage ? (
              <section className="section-slot about-shell">
                <h1 className="about-title">About Jobs Posted Today</h1>
                <p className="about-paragraph">
                  JobsPostedToday.com is a USA-based job platform built to help people discover
                  jobs posted today across the country in one place.
                </p>
                <h2 className="about-section-title">Helping You Find Jobs Near You</h2>
                <p className="about-paragraph">
                  Our search and filter experience helps users quickly discover jobs near me by
                  narrowing results with company, city, and location preferences.
                </p>
                <h2 className="about-section-title">Connecting You With Companies Hiring Near You</h2>
                <p className="about-paragraph">
                  The platform highlights employers hiring near me so candidates can focus on
                  active opportunities and apply faster.
                </p>
                <button className="about-back-btn" type="button" onClick={() => navigateTo('/')}>
                  Back to Jobs
                </button>
              </section>
            ) : isPrivacyPage ? (
              <section className="section-slot about-shell privacy-shell">
                <h1 className="about-title">Privacy Policy</h1>
                <p className="about-paragraph">
                  JobsPostedToday uses advertising, analytics, and platform infrastructure to
                  publish fresh job listings and improve site performance. This page explains what
                  data may be processed when you browse the site and how to manage available
                  privacy choices.
                </p>

                <h2 className="about-section-title">Advertising and Cookies</h2>
                <p className="about-paragraph">
                  The site may display Google AdSense advertisements. Google and its partners may
                  use cookies, local storage, and related technologies to measure performance,
                  limit repeat prompts, and personalize or de-personalize ads based on applicable
                  law and your choices.
                </p>

                <h2 className="about-section-title">Consent and Privacy Choices</h2>
                <p className="about-paragraph">
                  For regions where consent or opt-out flows apply, JobsPostedToday uses Google
                  Privacy &amp; Messaging tools to present the relevant choice experience. You can
                  reopen that experience from the Privacy Choices button below when it is available
                  for your region.
                </p>
                <div className="privacy-action-row">
                  <button className="about-back-btn" type="button" onClick={handleOpenPrivacyChoices}>
                    Privacy Choices
                  </button>
                </div>

                <h2 className="about-section-title">What We Process</h2>
                <p className="about-paragraph">
                  We may process search text, selected filters, page navigation, approximate
                  region signals used for compliance flows, and technical information such as
                  browser type, device characteristics, and page interactions. We also process job
                  listing and blog content that you request from the site.
                </p>

                <h2 className="about-section-title">Your Rights and Contact</h2>
                <p className="about-paragraph">
                  Depending on where you live, you may have rights to request access, deletion, or
                  additional information about how your data is used. Privacy inquiries can be
                  directed to <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
                </p>

                <button className="about-back-btn" type="button" onClick={() => navigateTo('/')}>
                  Back to Jobs
                </button>
              </section>
            ) : isBlogPage ? (
              <section className="section-slot about-shell blog-shell">
          {routeInfo.kind === 'blog-index' ? (
            <>
              <h1 className="about-title">JobsPostedToday Blog</h1>
              <p className="about-paragraph blog-intro">
                Practical job-search guidance and reusable Top 10 jobs posted today roundups built
                from fresh listings.
              </p>
              <div className="blog-controls">
                <input
                  type="text"
                  className="blog-search-input"
                  placeholder="Search blog posts"
                  value={blogSearchText}
                  onChange={(event) => setBlogSearchText(event.target.value)}
                />
                <div className="blog-tag-row">
                  <button
                    type="button"
                    className={`blog-tag-filter ${blogTagFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setBlogTagFilter('all')}
                  >
                    All
                  </button>
                  {blogTagOptions.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className={`blog-tag-filter ${blogTagFilter === tag ? 'active' : ''}`}
                      onClick={() => setBlogTagFilter(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              {renderInlineAd('ad-inline-shell ad-inline-shell-blog-index')}
              <div className="blog-list">
                {filteredBlogPosts.length ? (
                  filteredBlogPosts.map((post) => (
                    <article className="blog-card" key={post.slug}>
                      <button
                        type="button"
                        className="blog-card-title"
                        onClick={() => navigateTo(`/blog/${post.slug}`)}
                      >
                        {post.title}
                      </button>
                      <div className="blog-card-meta">
                        <span>{formatDisplayDate(post.date)}</span>
                        <span>{post.readTime || '5 min read'}</span>
                      </div>
                      <p className="blog-card-description">{post.description}</p>
                      <div className="blog-card-tags">
                        {(post.tags || []).map((tag) => (
                          <span className="blog-tag-pill" key={`${post.slug}-${tag}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="no-results">No blog posts match your filter.</div>
                )}
              </div>
              <div className="blog-cta">
                <button className="about-back-btn" type="button" onClick={() => navigateTo('/')}>
                  Back to Job Postings
                </button>
              </div>
            </>
          ) : activeBlogPost ? (
            <>
              <nav className="blog-breadcrumbs" aria-label="Breadcrumb">
                <button type="button" onClick={() => navigateTo('/')}>
                  Home
                </button>
                <span>&gt;</span>
                <button type="button" onClick={() => navigateTo('/blog')}>
                  Blog
                </button>
                <span>&gt;</span>
                <span>{activeBlogPost.title}</span>
              </nav>
              <article className="blog-article">
                <h1 className="about-title blog-post-title">{activeBlogPost.title}</h1>
                <div className="blog-card-meta blog-post-meta">
                  <span>{formatDisplayDate(activeBlogPost.date)}</span>
                  <span>{activeBlogPost.readTime || '5 min read'}</span>
                </div>
                <div className="blog-card-tags">
                  {(activeBlogPost.tags || []).map((tag) => (
                    <span className="blog-tag-pill" key={`active-${activeBlogPost.slug}-${tag}`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div
                  className="blog-content"
                  dangerouslySetInnerHTML={{ __html: activeBlogPost.html || '' }}
                />
                {renderInlineAd('ad-inline-shell ad-inline-shell-blog-post')}
                <div className="blog-cta">
                  <button type="button" className="about-back-btn" onClick={() => navigateTo('/')}>
                    Browse fresh jobs posted today
                  </button>
                </div>
              </article>
              {relatedBlogPosts.length ? (
                <section className="blog-related">
                  <h2 className="about-section-title">Related posts</h2>
                  <div className="blog-list">
                    {relatedBlogPosts.map((post) => (
                      <article className="blog-card" key={`related-${post.slug}`}>
                        <button
                          type="button"
                          className="blog-card-title"
                          onClick={() => navigateTo(`/blog/${post.slug}`)}
                        >
                          {post.title}
                        </button>
                        <div className="blog-card-meta">
                          <span>{formatDisplayDate(post.date)}</span>
                          <span>{post.readTime || '5 min read'}</span>
                        </div>
                        <p className="blog-card-description">{post.description}</p>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
            <>
              <h1 className="about-title">Blog Post Not Found</h1>
              <p className="about-paragraph">
                This article does not exist or has been removed.
              </p>
              <button className="about-back-btn" type="button" onClick={() => navigateTo('/blog')}>
                Back to Blog
              </button>
            </>
          )}
        </section>
      ) : isJobDetailPage ? (
        <section className="content-layout">
          <section className="section-slot latest-shell content-left job-detail-shell">
            {jobDetailState.status === 'loading' ? (
              <div className="job-detail-card">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-meta" />
                <div className="skeleton-line skeleton-meta" />
              </div>
            ) : activeJobDetail ? (
              <article className="job-detail-card">
                <h1 className="job-detail-title">{activeJobDetail.title}</h1>
                <div className="job-meta job-detail-meta">
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">🏢</span>
                    {activeJobDetail.company}
                  </span>
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">📍</span>
                    {activeJobDetail.location}
                  </span>
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">💼</span>
                    {activeJobDetail.jobType} · {activeJobDetail.workMode}
                  </span>
                  {activeJobDetail.experienceDisplay ? (
                    <span className="job-meta-item job-meta-time job-meta-experience">
                      {activeJobDetail.experienceDisplay}
                    </span>
                  ) : null}
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">⏱</span>
                    {formatRelativeTime(activeJobDetail.postedtime)}
                  </span>
                </div>
                <p className="job-detail-summary">{activeJobDetail.summary}</p>
                <div className="job-detail-actions">
                  {activeJobDetail.applyUrl ? (
                    <a
                      className="apply-btn"
                      href={activeJobDetail.applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Apply Now
                    </a>
                  ) : (
                    <button className="apply-btn" type="button" disabled>
                      No Apply Link
                    </button>
                  )}
                </div>
              </article>
            ) : (
              <article className="job-detail-card">
                <h1 className="job-detail-title">This job is no longer available</h1>
                <p className="job-detail-summary">
                  {jobDetailState.message ||
                    'This listing has expired or was removed by the source. Please browse fresh jobs posted today.'}
                </p>
              </article>
            )}

            {renderInlineAd('ad-inline-shell ad-inline-shell-job-detail')}

            <section className="job-related-shell">
              <h2 className="about-section-title">More jobs like this</h2>
              <LatestJobsList
                jobs={relatedJobsForDetail.slice(0, 6)}
                isLoading={jobDetailState.status === 'loading' && !relatedJobsForDetail.length}
                emptyMessage="No related jobs available right now."
                onOpenJob={(job) => {
                  saveJobDetailToCache(job);
                  if (job?.detailPath) navigateTo(job.detailPath);
                }}
              />
            </section>

            <div className="job-detail-cta">
              <button type="button" className="about-back-btn" onClick={() => navigateTo('/')}>
                Browse more fresh jobs posted today
              </button>
            </div>
          </section>

        </section>
      ) : (
        <>
          <section className="section-slot search-shell-wrapper">
            <SearchPanel
              searchText={searchText}
              onSearchTextChange={setSearchText}
              onSearch={handleSearch}
              onGoHome={handleGoHome}
              companyOptions={companyOptions}
              cityOptions={cityOptions}
              standardTitleOptions={standardTitleOptions}
              activeFilters={activeFilters}
            />
        </section>

          <section className="content-layout">
            <section className="section-slot latest-shell content-left">
              {showLocationIntro ? (
                <div className="location-intro-shell">
                  <h1 className="location-page-title">{locationIntroTitle}</h1>
                  {routeInfo.kind === 'city' ? (
                    <div className="city-intro-block">
                      {cityIntroLines.map((line, index) => (
                        <p className="city-intro-line" key={`city-intro-${index}`}>
                          {line}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="location-page-text">{locationIntroText}</p>
                  )}
                  {routeInfo.kind === 'jobs-near-me' && nearMeStatus === 'resolving' ? (
                    <p className="location-page-note">Detecting your location to load nearby jobs...</p>
                  ) : null}
                </div>
              ) : null}
              {routeInfo.kind === 'home' ? (
                <h1 className="home-page-title">Jobs Posted Today</h1>
              ) : null}
              <div className="section-title">Latest Jobs Posted in the Last 24 Hours</div>
              {isSearchMode ? (
                <div className="search-summary">
                  {jobsLoaded
                    ? `Showing ${response?.count || 0} of ${response?.total || 0} results${
                        normalizedSearch ? ` for "${activeQuery.trim()}"` : ''
                      }.`
                    : 'Loading jobs...'}
                </div>
              ) : null}
              <LatestJobsList
                jobs={jobs}
                isLoading={!jobsLoaded}
                emptyMessage={emptyMessage}
                onOpenJob={(job) => {
                  saveJobDetailToCache(job);
                  if (job?.detailPath) navigateTo(job.detailPath);
                }}
              />
              {renderInlineAd('ad-inline-shell ad-inline-shell-job-list')}
              {totalItems > PAGE_SIZE ? (
                <div className="pagination-row">
                  <button
                    className="page-btn"
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  {pageNumbers.map((page, index) => (
                    <React.Fragment key={`page-${page}`}>
                      {index > 0 && pageNumbers[index - 1] + 1 !== page ? (
                        <span className="page-ellipsis">...</span>
                      ) : null}
                      <button
                        className={`page-btn ${page === currentPage ? 'active' : ''}`}
                        type="button"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
                  <button
                    className="page-btn"
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </section>

            {!hideTopDashboards ? (
              <section className="section-slot trend-stage trend-stage-side">
                <article className="trend-panel">
                  <div className="trend-panel-head">
                    <h3>Top Companies Hiring Today</h3>
                    {trendContextText ? <p className="trend-panel-subhead">{trendContextText}</p> : null}
                  </div>
                  <div className="trend-list">
                    {isTopLoading
                      ? Array.from({ length: 5 }).map((_, index) => (
                          <div className="trend-row trend-row-skeleton" key={`company-skeleton-${index}`}>
                            <span className="trend-rank">{index + 1}</span>
                            <span className="trend-name trend-name-skeleton" />
                            <span className="trend-jobs trend-jobs-skeleton" />
                          </div>
                        ))
                      : topCompanies.slice(0, 5).map((item, index) => {
                          const count = item?.count || 0;
                          const width = maxCompany ? Math.max((count / maxCompany) * 100, 18) : 18;
                          return (
                            <button
                              className="trend-row trend-row-action"
                              key={`company-modern-${index}`}
                              type="button"
                              onClick={() => handleTopCompanyClick(item.company)}
                              aria-label={`Show jobs for company ${item.company}`}
                              title={`Show jobs for ${item.company}`}
                            >
                              <div className="trend-row-fill" style={{ width: `${width}%` }} />
                              <span className="trend-rank">{index + 1}</span>
                              <span className="trend-name">{item.company}</span>
                              <span className="trend-jobs">{`${item.count} jobs`}</span>
                            </button>
                          );
                        })}
                  </div>
                </article>

                <article className="trend-panel">
                  <div className="trend-panel-head">
                    <h3>Top Cities Hiring Today</h3>
                    {trendContextText ? <p className="trend-panel-subhead">{trendContextText}</p> : null}
                  </div>
                  <div className="trend-list">
                    {isTopLoading
                      ? Array.from({ length: 5 }).map((_, index) => (
                          <div className="trend-row trend-row-skeleton" key={`city-skeleton-${index}`}>
                            <span className="trend-rank">{index + 1}</span>
                            <span className="trend-name trend-name-skeleton" />
                            <span className="trend-jobs trend-jobs-skeleton" />
                          </div>
                        ))
                      : topCities.slice(0, 5).map((item, index) => {
                          const count = item?.count || 0;
                          const width = maxCity ? Math.max((count / maxCity) * 100, 18) : 18;
                          return (
                            <button
                              className="trend-row trend-row-action"
                              key={`city-modern-${index}`}
                              type="button"
                              onClick={() => handleTopCityClick(item.location)}
                              aria-label={`Show jobs for city ${item.location}`}
                              title={`Show jobs for ${item.location}`}
                            >
                              <div className="trend-row-fill" style={{ width: `${width}%` }} />
                              <span className="trend-rank">{index + 1}</span>
                              <span className="trend-name">{item.location}</span>
                              <span className="trend-jobs">{`${item.count} jobs`}</span>
                            </button>
                          );
                        })}
                  </div>
                </article>
              </section>
            ) : null}
          </section>
        </>
      )}

            <footer className="section-slot footer-slot">
              <Footer onNavigate={navigateTo} onOpenPrivacyChoices={handleOpenPrivacyChoices} />
            </footer>
          </div>
        </div>

        {shouldRenderRailColumns ? (
          <aside className="site-rail site-rail-right" aria-label="Right advertisement rail">
            <AdSenseUnit
              slot={ADSENSE_RIGHT_RAIL_SLOT}
              width={160}
              height={600}
              responsive={false}
              enabled={canServeAds}
              label="Advertisement"
              wrapperClassName="ad-rail-shell"
              className="ad-unit-fixed"
            />
          </aside>
        ) : null}
      </div>
    </>
  );
};

export default App;
